import { HighContrastSelector, IModalStyles, IStyle, memoizeFunction, mergeStyleSets, useTheme } from '@fluentui/react';
import { IAppTheme } from 'common';
import { getBackdropFilterStyles } from 'common/utilities/BackdropFilterFallback';

import { DrawerPosition, IDrawerStyles } from './Drawer.Props';

const baseline = 8;
const size = {
  xl: baseline * 4,
  lg: baseline * 3,
  md: baseline * 2,
  sm: baseline,
  xs: baseline / 2,
} as const;

const borderRadius = 16;
const positionBottomStyle: IStyle = {
  alignSelf: 'flex-end',
  width: '100% !important',
  minWidth: '100% !important',
  maxWidth: '100% !important',
  height: 'auto !important',
  minHeight: '0px !important',
  // No maxHeight so flex items in drawer can wrap & be scrolled to.
  // Instead, set maxHeight on the body element when position = bottom
  // maxHeight: '70% !important',
  borderTopLeftRadius: borderRadius,
  borderTopRightRadius: borderRadius,
  transformOrigin: 'bottom',
};

const positionTopStyle: IStyle = {
  alignSelf: 'flex-start',
  width: '100% !important',
  minWidth: '100% !important',
  maxWidth: '100% !important',
  height: 'auto !important',
  minHeight: '0px !important',
  // Same as positionBottomStyle
  // maxHeight: '70% !important',
  borderBottomLeftRadius: borderRadius,
  borderBottomRightRadius: borderRadius,
  transformOrigin: 'top',
};
const positionLeftStyle: IStyle = {
  alignSelf: 'flex-start',
  height: '100% !important',
  minHeight: '100% !important',
  maxHeight: '100% !important',
  width: 'auto !important',
  minWidth: '0px !important',
  maxWidth: '70% !important',
  borderBottomRightRadius: borderRadius,
  borderTopRightRadius: borderRadius,
  transformOrigin: 'left',
};
const positionRightStyle: IStyle = {
  alignSelf: 'flex-start',
  height: '100% !important',
  minHeight: '100% !important',
  maxHeight: '100% !important',
  width: 'auto !important',
  minWidth: '0px !important',
  maxWidth: '70% !important',
  borderBottomLeftRadius: borderRadius,
  borderTopLeftRadius: borderRadius,
  transformOrigin: 'right',
};

export const getStyles = memoizeFunction(
  (theme: IAppTheme, position: DrawerPosition, hideHandle: boolean, customStyles?: Partial<IDrawerStyles>) => {
    const horizontalPositioning = position === 'left' || position === 'right';
    return mergeStyleSets(
      {
        container: {
          height: '100%',
          overflow: 'hidden',
        } as IStyle,
        centered: {
          alignSelf: 'center',
        },
        handleContainer: {
          ...(horizontalPositioning ? { width: 20, height: '100%' } : { height: 20, width: '100%' }),
          alignSelf: 'center',
        } as IStyle,
        handleIcon: {
          backgroundColor: theme.appColors?.gray74ToAnchorTint20,
          borderRadius: 2,
          height: horizontalPositioning ? 36 : 4,
          width: horizontalPositioning ? 4 : 36,
          [HighContrastSelector]: {
            backgroundColor: 'ButtonText',
          },
        },
        body: {
          overflow: 'auto',
          paddingTop: position !== 'bottom' || hideHandle ? size.xl : size.sm,
          paddingLeft: position !== 'right' || hideHandle ? size.md : size.sm,
          paddingBottom: position !== 'top' || hideHandle ? size.xl : size.sm,
          paddingRight: position !== 'left' || hideHandle ? size.md : size.sm,
          maxHeight: position === 'bottom' || position === 'top' ? '70vh' : undefined,
        },
      },
      customStyles
    );
  }
);

export const getModalStyles = memoizeFunction(
  (
    theme: IAppTheme,
    position: DrawerPosition,
    animationLength: number,
    animationType: 'opening' | 'closing',
    isIOS: boolean
  ): Partial<IModalStyles> => {
    const commonStyles: Omit<IStyle, 'position'> = {
      position: 'static !important',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.white,
      boxShadow: theme.effects.elevation28,
      transition: `transform ${animationLength}ms ease`,
    };
    let transformStyle: string;
    let positionStyle: IStyle;
    let rootJustify = 'center';
    switch (position) {
      case 'bottom':
        positionStyle = positionBottomStyle;
        transformStyle = animationType === 'opening' ? 'translateY(0)' : 'translateY(100%)';
        break;
      case 'top':
        positionStyle = positionTopStyle;
        transformStyle = animationType === 'opening' ? 'translateY(0)' : 'translateY(-100%)';
        break;
      case 'left':
        positionStyle = positionLeftStyle;
        transformStyle = animationType === 'opening' ? 'translateX(0)' : 'translateX(-100%)';
        rootJustify = 'flex-start';
        break;
      default:
        positionStyle = positionRightStyle;
        transformStyle = animationType === 'opening' ? 'translateX(0)' : 'translateX(100%)';
        rootJustify = 'flex-end';
        break;
    }

    return {
      root: {
        justifyContent: rootJustify,
        ...getBackdropFilterStyles(theme, '8px', isIOS),
      },
      main: { ...commonStyles, ...positionStyle, transform: transformStyle },
      scrollableContent: { display: 'flex', flexDirection: 'column' },
    };
  }
);

const getModalOverlayStyles = memoizeFunction((isWebView: boolean, theme: IAppTheme) =>
  mergeStyleSets({
    root: {
      // Note: WebView needs an extra overlay in Dark Theme, as the second overlay is not present in WebView
      backgroundColor: isWebView ? theme.appColors?.white40ToBlack40 : theme.appColors?.dialogOverlayBackground,
    },
  })
);

export const useModalOverlayStyles = (isWebView: boolean) => {
  const theme = useTheme();
  return getModalOverlayStyles(isWebView, theme);
};

export const getContextualMenuDrawerStyles = memoizeFunction(() => {
  return mergeStyleSets({
    calloutRoot: {
      boxShadow: 'none',
      maxHeight: '100% !important',
      position: 'static',
    } as IStyle,
    item: {
      paddingLeft: 15,
    },
  });
});
